<template>
  <div class="records-page">
    <RecordFilterBar v-model:category="filterModule" v-model:time="filterTime" />
    <div class="rec-hint"><SiteTimeHint /></div>

    <div v-if="loading && !records.length" class="record-list">
      <PromoSkeleton variant="row" :count="6" />
    </div>

    <div v-else-if="records.length" class="record-list">
      <RecordRow
        v-for="(item, idx) in records"
        :key="item.record_id"
        :style="{ '--i': Math.min(idx % 20, 8) }"
        :item="item"
      />
      <button
        v-if="hasMore"
        type="button"
        class="load-more-btn"
        :disabled="loading"
        @click="loadMore"
      >
        {{ loading ? t('promo.records.loading') : t('promo.records.loadMore') }}
      </button>
    </div>

    <div v-else class="rec-empty">{{ t('promo.records.empty') }}</div>

    <div class="rec-foot">{{ t('promo.records.footer') }}</div>
  </div>
</template>

<script setup lang="ts">
import { fetchUnifiedClaims } from '~/api/promo'
import type { UnifiedClaimModule, UnifiedClaimRecordDto } from '~/types/api/promo'
import type { RecordFilterModule, RecordTimeRange } from './types'
import PromoSkeleton from '../components/PromoSkeleton.vue'
import RecordFilterBar from './components/RecordFilterBar.vue'
import RecordRow from './components/RecordRow.vue'

const { t } = useI18n()

const filterModule = ref<RecordFilterModule>('all')
const filterTime = ref<RecordTimeRange>('last_7d')

const records = ref<UnifiedClaimRecordDto[]>([])
const nextCursor = ref('')
const hasMore = ref(false)
const loading = ref(false)

/** 请求序号：换筛选后丢弃旧筛选的在途结果，免得把旧游标留给新筛选（服务端会 400） */
let fetchSeq = 0

/**
 * 时间窗口直接传 time_range，由服务端按站点时区切日（左闭右开），与充提 / 账单同口径。
 * next_cursor 是不透明串（base64url），只原样回填，不解析；翻页原样带上首页的 module / time_range
 * （游标里钉着首页请求，换筛选必须从首页重取）。
 */
const fetchPage = async (cursor?: string) => {
  const seq = ++fetchSeq
  loading.value = true
  try {
    const page = await fetchUnifiedClaims({
      cursor: cursor || undefined,
      page_size: 20,
      module: filterModule.value === 'all' ? undefined : (filterModule.value as UnifiedClaimModule),
      time_range: filterTime.value,
    })
    if (seq !== fetchSeq) return
    const deduped = cursor ? dedup([...records.value, ...page.items]) : page.items
    records.value = deduped
    nextCursor.value = page.next_cursor ?? ''
    hasMore.value = page.has_more
  } catch (e) {
    console.error('Fetch unified claims failed:', e)
  } finally {
    if (seq === fetchSeq) loading.value = false
  }
}

/** record_id 去重 */
const dedup = (items: UnifiedClaimRecordDto[]) => {
  const seen = new Set<string>()
  return items.filter(i => {
    if (seen.has(i.record_id)) return false
    seen.add(i.record_id)
    return true
  })
}

const loadMore = () => {
  if (loading.value || !hasMore.value) return
  fetchPage(nextCursor.value)
}

watch([filterModule, filterTime], () => {
  records.value = []
  nextCursor.value = ''
  hasMore.value = false
  fetchPage()
})

// 首屏请求放到挂载后：setup 顶层发请求会在服务端白发一次
onMounted(() => {
  fetchPage()
})
</script>

<style scoped lang="scss">
.record-list {
  margin-top: 8px;
  background: var(--surface);
  border: 1px solid var(--hairline);
  border-radius: var(--r-lg);
  overflow: hidden;
}

.rec-empty {
  display: flex;
  min-height: 160px;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
  border: 1px solid var(--hairline);
  border-radius: var(--r-lg);
  color: var(--muted);
  font-size: 12.5px;
}

.load-more-btn {
  display: block;
  width: 100%;
  padding: 14px;
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  color: var(--primary);
  border-top: 1px solid var(--hairline);
  background: transparent;

  &:disabled {
    color: var(--muted);
    cursor: default;
  }
}

.rec-hint {
  padding: 0 14px;
}

.rec-foot {
  text-align: center;
  font-size: 11px;
  color: var(--muted);
  padding: 14px;
  margin-bottom: 14px;
}
</style>
